import { useCallback, useRef } from 'react';
import { useTour } from '@reactour/tour';
import { differenceInMinutes, parseISO } from 'date-fns';
import { useExperiment } from '@hungryroot/experiments';
import { useNextOrderV3 } from '@hungryroot/orders';
import {
  OnboardingProgress,
  useOnboardingProgress,
} from '../../components/cart/OnboardingPopover/OnboardingProgress';

/** Used as a placeholder so the tour doesn't target or highlight any elements. */
// This is needed here to prevent circular deps.
export const NON_EXISTENT_STEP_SELECTOR =
  '#non-existent-selector-used-as-placeholder';

/**
 * Makes use of some onboarding progress state from the control to determine
 * whether to start this tour specific to experiment 188.
 */
const validOnboardingProgressForTourStart = new Set<string>([
  OnboardingProgress.ShowCustomizeModal,
  OnboardingProgress.ShowFirstPopover,
  OnboardingProgress.ShowSecondPopover,
]);

/**
 * Triggers the first order tour if the user meets the criteria. Can be called
 * multiple times but the tour will only be triggered once. Best triggered on the
 * component highlighted on the first step of the tour.
 */
export function useTriggerFirstOrderTour() {
  const discoveryHomeVariant = useExperiment(188);
  const { order } = useNextOrderV3(
    {},
    {
      enabled: discoveryHomeVariant === 1,
    }
  );
  const { getOnboardingProgress } = useOnboardingProgress(order) || {};
  const { setIsOpen, isOpen, steps } = useTour();
  const triggeredTourRef = useRef(isOpen);

  const onboardingProgress = getOnboardingProgress?.();

  const shouldStartTour =
    discoveryHomeVariant === 1 &&
    typeof onboardingProgress === 'string' &&
    validOnboardingProgressForTourStart.has(onboardingProgress) &&
    order &&
    differenceInMinutes(parseISO(order.cutoff_datetime), new Date()) > 0 &&
    order.sequence === 1;

  const triggerFirstOrderTour = useCallback(() => {
    if (shouldStartTour && !triggeredTourRef.current && steps.length > 0) {
      let selector = steps[0].selector as string;

      if (selector === NON_EXISTENT_STEP_SELECTOR) {
        selector = steps[1].selector as string;
      }

      if (document.querySelector(selector)) {
        setIsOpen(true);
        triggeredTourRef.current = true;
      }
    }
  }, [setIsOpen, shouldStartTour, steps]);

  return {
    triggerFirstOrderTour,
  };
}
